import { useMemo } from "react";
import {
  BasicBotData,
  BotType,
  PrecisionType,
  RawDCAStrategyData,
} from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { getOrdersPrices } from "@shared/features/bots/BotForm/common/utils";
import { skipToken } from "@reduxjs/toolkit/query";
import { sharedLib } from "@shared/services/shared.service";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";

/** Where the live price is against a grid bot's range. */
export type GridRangeState = "inside" | "above" | "below" | "unknown";

export interface NewLookGridRange {
  /** The grid's lowest and highest price. */
  min: number;
  max: number;
  /** Every grid level, ascending (the base price included). */
  levels: number[];
  /** The live price, when the socket has one. */
  price?: number;
  /** The price as a 0–1 share of the range (clamped), when known. */
  position?: number;
  state: GridRangeState;
  /** The pair's price precision (decimals), when known. */
  pricePrecision?: number;
}

/**
 * New look: a grid bot's range, derived from the bot's own strategy (the
 * grid's base price and its price steps, as the bot form shows them) and the
 * live price. Undefined for other bot types or when the range can't be
 * worked out. Nothing is guessed: no strategy, no range.
 */
const useNewLookGridRange = (
  botData: BasicBotData
): NewLookGridRange | undefined => {
  const isGrid = botData.bot_type === BotType.GRID;
  const symbol = isGrid ? botData.symbols?.[0] : undefined;
  const exchange = isGrid ? botData.exchange : undefined;
  const { socketPrice } = usePricesSocket(symbol, exchange);
  const { precision } = usePrecisions(
    symbol && exchange
      ? { symbol, exchange, precisionType: PrecisionType.PRICE }
      : undefined
  );
  // The bots list may leave the strategy out: then the bot's own data (the
  // query the bot drawer reads, so opening the drawer is instant) has it.
  const needsBotData =
    isGrid && (!botData.buying_strategy || !botData.max_grid_price);
  const { currentData: fullBotData } =
    sharedLib.sharedApi.bots.useGetBotDataQuery(
      needsBotData ? { dca_bot_id: botData.id } : skipToken
    );
  const source = fullBotData?.data ?? botData;
  // A grid's entries are a DCA-style strategy (a base price and its steps).
  const strategy = source.buying_strategy as
    | Partial<RawDCAStrategyData>
    | undefined;
  const basePrice = source.max_grid_price;
  const reverse = !!source.reverse;
  const livePrice = socketPrice.value;
  // Before the prices socket answers: the price the bots list was computed
  // at, back from a next order's price and its distance from it.
  const nextOrder =
    botData.active_trade?.next_take_profit ?? botData.active_trade?.next_entry;
  const snapshotPrice =
    nextOrder && nextOrder.distance > -1
      ? nextOrder.price / (1 + nextOrder.distance)
      : undefined;

  return useMemo(() => {
    if (!isGrid || !strategy || !basePrice) return undefined;
    const levels = getOrdersPrices(
      strategy.number_of_orders,
      // The API sends the step as a ratio (0.02), the helper takes percent.
      typeof strategy.price_difference === "number"
        ? strategy.price_difference * 100
        : undefined,
      strategy.price_scale,
      strategy.baseline,
      basePrice,
      precision || 0,
      OrderTypes.ENTRY,
      reverse
    )
      .filter((price) => Number.isFinite(price) && price > 0)
      .sort((a, b) => a - b);
    if (levels.length < 2) return undefined;
    const min = levels[0];
    const max = levels[levels.length - 1];
    if (max <= min) return undefined;
    const pricePrecision = precision || undefined;
    const known = livePrice || snapshotPrice;
    const price = known && known > 0 ? known : undefined;
    if (price === undefined) {
      return { min, max, levels, state: "unknown" as const, pricePrecision };
    }
    const state: GridRangeState =
      price > max ? "above" : price < min ? "below" : "inside";
    return {
      min,
      max,
      levels,
      price,
      position: Math.min(Math.max((price - min) / (max - min), 0), 1),
      state,
      pricePrecision,
    };
  }, [
    isGrid,
    strategy,
    basePrice,
    reverse,
    precision,
    livePrice,
    snapshotPrice,
  ]);
};

export default useNewLookGridRange;
